import React, { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { LogoLoading } from '@/shared/ui/custom/logo-loading';

interface BaseLayoutProps {
  children: React.ReactNode;
  fullHeight?: boolean; // For pages like interactive viewer that need fixed viewport height
  isLoading?: boolean; // Show logo loading animation
  loadingMessage?: string; // Optional message to display while loading
  loadingVariant?: 'fade' | 'pulse' | 'breathing'; // Animation variant
  loadingSize?: 'sm' | 'md' | 'lg' | 'xl'; // Logo size
}

const BaseLayout: React.FC<BaseLayoutProps> = ({ 
  children, 
  fullHeight = false,
  isLoading = false,
  loadingMessage,
  loadingVariant = 'breathing',
  loadingSize = 'xl'
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('iv-app-shell');
    const setViewportHeight = () => {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--app-vh', `${viewportHeight * 0.01}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('pageshow', setViewportHeight);
    const onVisibilityChange = () => {
      if (!document.hidden) setViewportHeight();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.visualViewport?.addEventListener('resize', setViewportHeight);
    window.visualViewport?.addEventListener('scroll', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('pageshow', setViewportHeight);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.documentElement.classList.remove('iv-app-shell');
      window.visualViewport?.removeEventListener('resize', setViewportHeight);
      window.visualViewport?.removeEventListener('scroll', setViewportHeight);
    };
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className={`slate-glow bg-background text-foreground ${fullHeight ? 'h-[calc(var(--app-vh,1vh)*100)] overflow-hidden' : 'min-h-[calc(var(--app-vh,1vh)*100)]'}`}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main content wrapper */}
      <div className={`flex flex-col lg:pl-[17rem] ${fullHeight ? 'h-full min-h-0' : 'min-h-[calc(var(--app-vh,1vh)*100)]'}`}>
        {/* Topbar */}
        <Topbar onMenuClick={toggleSidebar} />
        
        {/* Main content */}
        <main className={fullHeight ? 'min-h-0 flex-1 overflow-hidden' : 'flex-1'}>
          {isLoading ? (
            <div className={`${fullHeight ? 'h-full' : 'min-h-[50vh]'} flex items-center justify-center`}>
              <LogoLoading 
                variant={loadingVariant}
                size={loadingSize}
                message={loadingMessage}
              />
            </div>
          ) : (
            children
          )}
        </main>
      </div>
    </div>
  );
};

export default BaseLayout; 
